import { useMemo } from "react";
import { Link } from "react-router-dom";

import { useDLMApplicationView } from "../../context/DLMApplicationViewProvider";
import { getDLMApplicationTypeText, getDLMWorkflowCode } from "../../_helpers";
import { 
  DetailCardShell, DetailCardHeader, DetailCardBody,
  IndexSelector,
  DetailItem,
  DetailLabelKeysToCells,
  DetailLabelKeysToFiles,
  DetailFileDownload,
  extractDOCDetailFiles,
  extractROIDetailFiles
} from "../../../../../components/layout"


import { filter, sumBy, findIndex } from "lodash";
import { useAuth } from "../../../../auth";
import { isPopulatedArray, showDate } from "../../../../../components/helpers";


const DLM_SITE_DETAILS_COMMON_FIELDS = {
  projectName: 'Project Name',
  projectLocation: 'Project Location',
  installerName: 'Installer Company',
  scdfProjectPlanNo: 'SCDF Building Plan No',
  bpNumber: 'SCDF Building Plan No',
  clientRefNo: 'Client Ref No',
  otherReference: 'Other Reference (DSTA No)',    
}

const DLM_SITE_ATTACHMENTS = {
  prototypeTestReport: 'Prototype Test Report',
  trainingRecord: 'Training Record',
  methodOfStatement: 'Method of Statement',
  productConstructionDrawing: 'Product Construction Drawing',
  sitePlanDrawing: 'Site Plan Drawing',
  siteChecklist: 'Site Checklist',
  setscoROI: 'Setsco ROI'
}

const Sites = () => {

  const { currentUser } = useAuth(); 

  const { dlmAppData: dat, siteIDs, siteIndex, setSiteIndex, selectedSite, setSelectedSite } = useDLMApplicationView();

  const { dlmAppId, sites, dlmTypeType, inspectionLevel, appTypeText, workflow } = useMemo(() => {
    if (dat === undefined) return { dlmAppId: 0, sites : [], dlmTypeType: '', inspectionLevel: 0, appTypeText: '', workflow: ''};

    const { id: dlmAppId, siteApplications, dlmType, applicationType } = dat;
    
    const sites = siteApplications && siteApplications.length > 0 ? filter( siteApplications,  (o) => { return (! o.deleted); }) : null;
    const dlmTypeType = (dlmType && dlmType?.type) ? dlmType.type : '';
    const inspectionLevel = (dlmType && dlmType.hasOwnProperty('inspectionLevel')) ? dlmType.inspectionLevel : '';
    
    const appTypeText = getDLMApplicationTypeText(applicationType, dlmType);
    const workflow = getDLMWorkflowCode(dat);

    return { dlmAppId, sites, dlmTypeType, inspectionLevel, appTypeText, workflow };

  }, [dat])

  const {labelQuantity, inspectionID, inspectionRefNo, showFiles} = useMemo(() => {

    let labelQuantity = 0;
    if (selectedSite && dlmTypeType === 'TYPE_LABEL' && selectedSite?.rois && selectedSite.rois.length > 0) {
      labelQuantity = sumBy(selectedSite.rois, o => (o.quantity));
    }

    let inspectionID = 0;
    let inspectionRefNo = '';

    if (selectedSite && selectedSite?.siteInspection) {
      inspectionID = selectedSite.siteInspection?.id ? selectedSite?.siteInspection.id : 0;
      inspectionRefNo = selectedSite.siteInspection?.refNo ? selectedSite.siteInspection.refNo : '';    
    }

    let showFiles = [];
    if (selectedSite?.docs && selectedSite.docs.length > 0) {
      showFiles.push(...extractDOCDetailFiles(selectedSite.docs, dlmAppId));
    }
    if (selectedSite?.rois && selectedSite.rois.length > 0) {
      showFiles.push(...extractROIDetailFiles(selectedSite.rois, dlmAppId));
    }

    return { labelQuantity, inspectionID, inspectionRefNo, showFiles }

  },[dlmAppId, selectedSite, dlmTypeType]);

  const dlmBasePath = useMemo(() => {
    return (currentUser?.isSetsco) ? '/setsco/doclabel-management' : '/company/doclabel';
  }, [currentUser]);

  if (! (dat !== undefined && sites && sites.length > 0)) {
    return null;
  }

  return(
  <DetailCardShell>
    <DetailCardHeader>
      <div className="flex-grow-1 d-flex flex-row align-items-center gap-4 justify-content-between">
        <div className="">
          <h3>Site Details</h3>
        </div>
        { (sites.length > 1) &&
        <IndexSelector
          itemCount={sites.length}
          currentIndex={siteIndex}
          displayCount={5}
          onChangeIndex={(idx: number) => {
              setSiteIndex(idx);
              const _idx = findIndex(dat.siteApplications, { id : siteIDs[idx]});
              setSelectedSite(dat.siteApplications[_idx]);
          }}
        />
        }
      </div>
    </DetailCardHeader>
    <DetailCardBody>
    { selectedSite &&
      <>
      {DetailLabelKeysToCells(DLM_SITE_DETAILS_COMMON_FIELDS, selectedSite)}
      { (dlmTypeType === 'TYPE_DOC') &&
      <>
      { dat?.coverageArea &&
      <DetailItem labelText="Coverage Area" content={`${dat.coverageArea} ${dat.unit}`} />
      }
      { (appTypeText === 'Site Inspection' && dat.hasOwnProperty('percentCompletion')) &&
      <DetailItem labelText="Percentage Completion">
        <div className="d-flex flex-row align-items-center gap-4 pe-16">
          <div>
            {`${dat.percentCompletion}%`}        
          </div>
        </div>        
      </DetailItem>      
      }
      </>
      }      
      { (dlmTypeType === 'TYPE_LABEL' && labelQuantity > 0) && 
      <DetailItem labelText="Label Quantity" content={`${labelQuantity}`} />
      }      
      { (appTypeText === 'Site Inspection') && 
        <DetailItem labelText="Inspection Level" content={`${inspectionLevel}`} />
      }      
      { (selectedSite?.inspectionDate) &&
      <DetailItem labelText={ ['Label1b2','Label1b3'].includes(workflow) ? 'Site Labelling Date' : "Inspection Date"}>{showDate(selectedSite.inspectionDate, 'inspectionDate')}</DetailItem>
      }
      <div className='separator border-gray-200 mb-2'></div>
      
      { (isPopulatedArray(showFiles)) && 
      showFiles.map((dat, idx) => (
      <DetailFileDownload key={`showfile${idx}`} labelText={dat.showFileTitle} file={dat.showFile} hasChanged={dat.hasChanged} data={dat} canReplace />
      ))}   

      { (inspectionID > 0 || inspectionRefNo !== '') && 
        <DetailItem labelText="Site Inspection Reference">
          {inspectionRefNo}
          &nbsp; &nbsp;
          { (inspectionID > 0) &&
            <Link to={`${dlmBasePath}/siteinspection-view/${inspectionID}`}>(view)</Link>
          }                
        </DetailItem>
      }      
      {(
        selectedSite?.prototypeTestReport ||
        selectedSite?.trainingRecord ||
        selectedSite?.methodOfStatement ||
        selectedSite?.productConstructionDrawing ||
        selectedSite?.sitePlanDrawing ||
        selectedSite?.siteChecklist ||
        selectedSite?.setscoROI) &&
      <>
      <label className="mt-4 fs-5 fw-bold">Attachments</label>
      <div className='separator border-gray-200 mb-2' />
      <div className="row">
        {DetailLabelKeysToFiles(DLM_SITE_ATTACHMENTS, selectedSite)}
      </div>
      </>
      }            
      </>
      }      
    </DetailCardBody>
  </DetailCardShell>        
  )
}

export { Sites } 